import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMessage } from "../lib/api";
import {
  ArrowRight,
  Building2,
  Flame,
  Snowflake,
  Target,
  Wallet,
  Users,
  TrendingUp,
  Activity,
  X,
  Sparkles,
  Calculator,
  Quote,
  Lock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PROPERTY_TYPES } from "../lib/propertyTypes";
import { DISTRESS_SIGNALS } from "../lib/distressSignals";

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

// [real] swap with real customer testimonials when you have them
const TESTIMONIALS = [
  {
    quote: "Found 3 deals in my first week that my MLS searches had completely missed. WholesaleHQ pulls signals nobody else aggregates.",
    name: "Marcus Reyes",
    role: "Wholesaler · Phoenix, AZ",
  },
  {
    quote: "The motivation score saved me 20 hours of cold calling. I only contact 78+ scores now and my pickup rate doubled.",
    name: "Jenna Whitfield",
    role: "Investor · Tampa, FL",
  },
  {
    quote: "Auto-blast to my buyers list closed a $14k assignment in 36 hours. The map view alone is worth the subscription.",
    name: "Daniel Park",
    role: "Wholesaler · Dallas, TX",
  },
];

// [real] hook to the actual /api/markets/analyze for these once you've curated featured markets
const QUICK_PICKS = [
  { city: "Greeley", county: "Weld", state: "CO", tag: "hot", metric: "High motivation", description: "498 sold last 180d · +0.18% jobs" },
  { city: "Tulsa", county: "Tulsa", state: "OK", tag: "cold", metric: "Low saturation", description: "Few investors per capita" },
  { city: "Cleveland", county: "Cuyahoga", state: "OH", tag: "hot", metric: "High distress", description: "Strong pre-foreclosure flow" },
  { city: "Pueblo", county: "Pueblo", state: "CO", tag: "cold", metric: "Cash-buyer rich", description: "55% cash transactions" },
  { city: "Birmingham", county: "Jefferson", state: "AL", tag: "hot", metric: "High equity", description: "Long-tenure owners + low DOM" },
  { city: "Memphis", county: "Shelby", state: "TN", tag: "cold", metric: "Underrated", description: "Low investor saturation" },
];

const METRICS = [
  {
    id: "motivation",
    label: "Motivation Index",
    icon: Flame,
    tooltip: "Weighted 0–100 score per property. Combines distress signals (foreclosure, eviction, tax-delinquent), equity, owner tenure, and absentee status. 75+ = high motivation.",
  },
  {
    id: "saturation",
    label: "Saturation Score",
    icon: Snowflake,
    tooltip: "How many active investors are working a market. Lower = less competition = your offer stands out.",
  },
  {
    id: "cash",
    label: "Cash Landlord %",
    icon: Wallet,
    tooltip: "Share of recent sales that closed with cash. Higher cash % means more landlord activity & easier dispositions.",
  },
  {
    id: "activity",
    label: "Investor Activity",
    icon: Activity,
    tooltip: "Trailing 6mo wholesale-style transactions — quick flips, off-market sales, deed-to-LLC. Higher = active market.",
  },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [state, setState] = useState("");
  const [types, setTypes] = useState([]);
  const [signals, setSignals] = useState([]);
  const [showSample, setShowSample] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoData, setDemoData] = useState(null);
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [advisorProp, setAdvisorProp] = useState(null);
  const [advisorText, setAdvisorText] = useState("");
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [advisorLocked, setAdvisorLocked] = useState(false);
  const ctaRef = useRef(null);
  const demoRef = useRef(null);

  const toggle = (setter) => (id) =>
    setter((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const buildMarketUrl = () => {
    const params = new URLSearchParams({ city: city.trim(), county: county.trim(), state });
    if (types.length) params.set("types", types.join(","));
    if (signals.length) params.set("signals", signals.join(","));
    return `/market?${params.toString()}`;
  };

  const runRecon = async () => {
    if (!city.trim() || !state) {
      toast.error("Enter a city and state to start");
      ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // Already paying customer — skip the demo, go to full results
    if (user) {
      nav(buildMarketUrl());
      return;
    }
    // Public demo path
    setDemoLoading(true);
    try {
      const r = await api.post("/public/preview-market", {
        city: city.trim(),
        state,
        county: county.trim(),
      });
      setDemoData(r.data);
      setTimeout(() => demoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error("Demo limit reached — sign up to keep going");
        nav(`/register?next=${encodeURIComponent(buildMarketUrl())}`);
      } else {
        toast.error(errMessage(e, "Preview failed — please try again"));
      }
    } finally {
      setDemoLoading(false);
    }
  };

  const unlockResults = () => {
    nav(`/register?next=${encodeURIComponent(buildMarketUrl())}`);
  };

  const askAdvisor = async (prop) => {
    setAdvisorProp(prop);
    setAdvisorText("");
    setAdvisorOpen(true);
    setAdvisorLocked(false);
    setAdvisorLoading(true);
    try {
      const r = await api.post("/public/preview-summary", { property_data: prop });
      setAdvisorText(r.data.summary || "");
    } catch (e) {
      if (e?.response?.status === 429) {
        setAdvisorLocked(true);
        setAdvisorText(
          "You've used your free AI advisor previews. Sign up to unlock unlimited 'Why is this a deal?' breakdowns on every property.",
        );
      } else {
        toast.error(errMessage(e, "AI advisor unavailable — try again"));
        setAdvisorOpen(false);
      }
    } finally {
      setAdvisorLoading(false);
    }
  };

  const applyQuickPick = (q) => {
    setCity(q.city);
    setCounty(q.county);
    setState(q.state);
    setTimeout(() => ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  return (
    <div className="bg-[--bg] text-[--ink] min-h-screen">
      {/* nav */}
      <header className="border-b border-[--line] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <Building2 size={18} strokeWidth={1.5} />
            <span className="font-display font-bold tracking-tight">WholesaleHQ</span>
          </a>
          <div className="flex items-center gap-3 text-sm">
            <a href="/pricing" className="text-[--ink-soft] hover:text-[--ink]">
              Pricing
            </a>
            {user ? (
              <a href="/dashboard" className="btn-primary !py-1.5 !px-3 text-xs">
                Dashboard
              </a>
            ) : (
              <>
                <a href="/login" className="text-[--ink-soft] hover:text-[--ink]">
                  Sign in
                </a>
                <a href="/register" data-testid="nav-cta-register" className="btn-primary !py-1.5 !px-3 text-xs">
                  Start free
                </a>
              </>
            )}
          </div>
        </div>
      </header>

      {/* SECTION 1 — HERO */}
      <section ref={ctaRef} className="border-b border-[--line]">
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="overline mb-4">// Market Recon</div>
            <h1 className="font-display text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
              Find the most profitable markets
              <br />
              <span className="text-[--accent]">before you spend a dollar</span>
              <br />
              on marketing.
            </h1>
            <p className="text-base text-[--ink-soft] mt-5 max-w-md leading-relaxed">
              AI-powered market intelligence that reveals motivation, saturation, and investor
              activity in seconds. Real data from ATTOM, RentCast, BLS, and the Census Bureau.
            </p>
            <div className="flex items-center gap-4 mt-6 text-xs text-[--ink-muted] font-mono">
              <span className="flex items-center gap-1">
                <span className="w-1 h-1 bg-emerald-500 rounded-full" />
                Live data
              </span>
              <span>·</span>
              <span>No credit card to preview</span>
              <span>·</span>
              <span>14-day trial</span>
            </div>
          </div>

          <div className="surface-card p-6 lg:p-7">
            <div className="overline mb-4">Quick analysis</div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <input
                data-testid="hero-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City *"
                className="col-span-2 text-sm"
              />
              <select
                data-testid="hero-state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="text-sm font-mono"
              >
                <option value="">State *</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <input
              data-testid="hero-county"
              value={county}
              onChange={(e) => setCounty(e.target.value)}
              placeholder="County (optional, enables job & population scoring)"
              className="text-sm w-full mb-4"
            />

            <div className="mb-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[--ink-muted] mb-2">
                Property types
              </div>
              <div className="flex flex-wrap gap-1.5">
                {PROPERTY_TYPES.map((t) => {
                  const on = types.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`hero-type-${t.id}`}
                      onClick={() => toggle(setTypes)(t.id)}
                      className={`text-[11px] px-2.5 py-1 border ${
                        on
                          ? "bg-[--ink] text-white border-[--ink]"
                          : "bg-white text-[--ink] border-[--line] hover:border-[--ink]"
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-5">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[--ink-muted] mb-2">
                Distress signals · match any
              </div>
              <div className="flex flex-wrap gap-1.5">
                {DISTRESS_SIGNALS.map((s) => {
                  const on = signals.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`hero-signal-${s.id}`}
                      onClick={() => toggle(setSignals)(s.id)}
                      className={`text-[11px] px-2.5 py-1 border ${
                        on
                          ? "bg-[--accent] text-white border-[--accent]"
                          : "bg-white text-[--ink] border-[--line] hover:border-[--accent]"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              data-testid="hero-run-recon"
              type="button"
              onClick={runRecon}
              disabled={demoLoading}
              className="btn-primary w-full text-sm font-semibold flex items-center justify-center gap-2 py-3 disabled:opacity-60"
            >
              {demoLoading ? (
                "Running recon…"
              ) : (
                <>
                  Run Market Recon
                  <ArrowRight size={16} strokeWidth={2} />
                </>
              )}
            </button>
            <button
              data-testid="hero-sample-report"
              type="button"
              onClick={() => setShowSample(true)}
              className="block w-full text-center text-xs text-[--ink-soft] hover:text-[--ink] mt-3 underline"
            >
              See sample report
            </button>
          </div>
        </div>
      </section>

      {/* DEMO RESULTS — appears after Run Market Recon */}
      {demoData && (
        <section ref={demoRef} className="border-b border-[--line]" data-testid="demo-results">
          <div className="max-w-6xl mx-auto px-6 py-14">
            <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
              <div>
                <div className="overline mb-3">// Live recon preview</div>
                <h2 className="font-display text-3xl font-bold tracking-tighter">
                  {city}, {state}
                  <span className="text-[--ink-muted] font-mono text-base ml-2">
                    · {demoData.total_found} properties found
                  </span>
                </h2>
              </div>
              <div className="text-xs font-mono text-[--ink-muted]">
                {demoData.data_source?.startsWith("live") ? "Live data" : "Sample data"}
                {" · "}refreshed in real time
              </div>
            </div>

            {/* Market summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <DemoStat
                icon={Target}
                label="Opportunity"
                value={`${demoData.market_summary.opportunity_score}/100`}
                tone={
                  demoData.market_summary.opportunity_score >= 70
                    ? "good"
                    : demoData.market_summary.opportunity_score >= 50
                    ? "ok"
                    : "weak"
                }
              />
              <DemoStat
                icon={Flame}
                label="Motivation"
                value={demoData.market_summary.motivation_label}
                tone={
                  demoData.market_summary.motivation_label === "High"
                    ? "good"
                    : demoData.market_summary.motivation_label === "Medium"
                    ? "ok"
                    : "weak"
                }
              />
              <DemoStat
                icon={Snowflake}
                label="Saturation"
                value={demoData.market_summary.saturation_label || "Low"}
                tone={demoData.market_summary.saturation_label === "Low" ? "good" : "ok"}
              />
              <DemoStat
                icon={Wallet}
                label="Cash %"
                value={`${demoData.market_summary.cash_pct}%`}
              />
            </div>

            {/* Health stat row if available */}
            {(demoData.health.sales_velocity || demoData.health.jobs_yoy_pct != null) && (
              <div className="surface-card p-4 mb-6 flex items-center gap-6 flex-wrap text-xs font-mono">
                {demoData.health.sales_velocity && (
                  <span className="flex items-center gap-2">
                    <Activity size={12} strokeWidth={1.5} className="text-[--ink-muted]" />
                    <span className="text-[--ink-muted]">Sold 180d:</span>
                    <span className="font-semibold">{demoData.health.sales_velocity.total_sold_180d}</span>
                  </span>
                )}
                {demoData.health.population_cagr != null && (
                  <span className="flex items-center gap-2">
                    <Users size={12} strokeWidth={1.5} className="text-[--ink-muted]" />
                    <span className="text-[--ink-muted]">Pop CAGR:</span>
                    <span className={`font-semibold ${demoData.health.population_cagr >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {demoData.health.population_cagr >= 0 ? "+" : ""}
                      {demoData.health.population_cagr}%
                    </span>
                  </span>
                )}
                {demoData.health.jobs_yoy_pct != null && (
                  <span className="flex items-center gap-2">
                    <TrendingUp size={12} strokeWidth={1.5} className="text-[--ink-muted]" />
                    <span className="text-[--ink-muted]">Jobs YoY:</span>
                    <span className={`font-semibold ${demoData.health.jobs_yoy_pct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {demoData.health.jobs_yoy_pct >= 0 ? "+" : ""}
                      {demoData.health.jobs_yoy_pct}%
                    </span>
                  </span>
                )}
              </div>
            )}

            {/* Top 3 redacted previews */}
            <div className="overline mb-3">// Top 3 deals · click for AI breakdown</div>
            <div className="grid md:grid-cols-3 gap-3 mb-5">
              {demoData.top_previews.map((p, i) => (
                <DemoDealCard key={i} prop={p} onClick={() => askAdvisor(p)} />
              ))}
            </div>

            {/* Locked CTA card */}
            <div className="surface-card p-6 flex items-center gap-5 flex-wrap" data-testid="demo-unlock-card">
              <Lock size={20} strokeWidth={1.5} className="text-[--accent]" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-bold tracking-tighter">
                  {demoData.locked_count} more deals locked
                </div>
                <div className="text-sm text-[--ink-soft] mt-1">
                  Sign up to see full addresses, owner info, MAO calculators, listing agents, and AI outreach drafts.
                </div>
              </div>
              <button
                data-testid="demo-unlock-btn"
                onClick={unlockResults}
                className="btn-primary flex items-center gap-2 shrink-0"
              >
                Unlock all results <ArrowRight size={14} strokeWidth={2} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 2 — VALUE PREVIEW */}
      <section className="border-b border-[--line] bg-[--bg-soft]">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="overline mb-3">// What you get in one search</div>
          <h2 className="font-display text-3xl font-bold tracking-tighter mb-8">
            Five instant signals. Zero spreadsheets.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <PreviewCard icon={Flame} title="Top distressed ZIPs" sub="Sorted by motivation density" />
            <PreviewCard icon={Target} title="Motivation score" sub="Per-property 0–100" />
            <PreviewCard icon={Snowflake} title="Saturation score" sub="How crowded the market is" />
            <PreviewCard icon={Wallet} title="Cash buyer activity" sub="Recent investor purchases" />
            <BlurredDealCard />
          </div>
        </div>
      </section>

      {/* SECTION 3 — QUICK PICKS */}
      <section className="border-b border-[--line]">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline mb-3">// Quick picks</div>
              <h2 className="font-display text-3xl font-bold tracking-tighter">
                Featured markets right now
              </h2>
            </div>
            <div className="text-xs text-[--ink-muted] font-mono">tap to autofill</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="quick-picks">
            {QUICK_PICKS.map((q) => (
              <button
                key={`${q.city}-${q.state}`}
                type="button"
                data-testid={`quick-pick-${q.city.toLowerCase()}`}
                onClick={() => applyQuickPick(q)}
                className="surface-card p-4 text-left hover:border-[--ink] transition-colors group"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-display font-bold">{q.city}, {q.state}</div>
                    <div className="text-xs font-mono text-[--ink-muted] mt-0.5">{q.county} County</div>
                  </div>
                  <span
                    className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 flex items-center gap-1 ${
                      q.tag === "hot"
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-blue-50 text-blue-700 border border-blue-200"
                    }`}
                  >
                    {q.tag === "hot" ? "Hot" : "Cold"}
                  </span>
                </div>
                <div className="text-sm font-semibold mt-3">{q.metric}</div>
                <div className="text-xs text-[--ink-muted] mt-1">{q.description}</div>
                <div className="mt-3 text-[10px] font-mono text-[--ink-muted] uppercase tracking-wider flex items-center gap-1 group-hover:text-[--ink]">
                  Autofill <ArrowRight size={10} strokeWidth={2} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — METRIC TOOLTIP BAR */}
      <section className="border-b border-[--line] bg-[--bg-soft]">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="overline mb-4">// How we score</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="metric-bar">
            {METRICS.map((m) => (
              <MetricTooltip key={m.id} {...m} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — SOCIAL PROOF */}
      <section className="border-b border-[--line]">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="overline mb-3">// What operators are saying</div>
          <h2 className="font-display text-3xl font-bold tracking-tighter mb-8">
            Built by wholesalers, for wholesalers.
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="surface-card p-5" data-testid={`testimonial-${i}`}>
                <Quote size={14} strokeWidth={1.5} className="text-[--ink-muted] mb-3" />
                <p className="text-sm leading-relaxed mb-4">{t.quote}</p>
                <div className="border-t border-[--line] pt-3">
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-xs font-mono text-[--ink-muted]">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-b border-[--line]">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter mb-4">
            Start hunting smarter. <span className="text-[--accent]">Free for 14 days.</span>
          </h2>
          <p className="text-base text-[--ink-soft] max-w-md mx-auto mb-7">
            Card required to start the trial. Cancel anytime — no charge until day 15.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              data-testid="final-cta-start"
              onClick={() => (user ? nav("/dashboard") : nav("/register"))}
              className="btn-primary px-7 py-3 font-semibold flex items-center gap-2"
            >
              Start free trial
              <ArrowRight size={16} strokeWidth={2} />
            </button>
            <button
              onClick={() => setShowSample(true)}
              className="btn-ghost px-6 py-3"
            >
              See sample report
            </button>
          </div>
        </div>
      </section>

      <footer className="bg-[--bg-soft]">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-[--ink-muted] font-mono flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} WholesaleHQ</span>
          <div className="flex gap-4">
            <a href="/pricing" className="hover:text-[--ink]">Pricing</a>
            <a href="/login" className="hover:text-[--ink]">Sign in</a>
            <a href="mailto:hello@wholesalehq.io" className="hover:text-[--ink]">Contact</a>
          </div>
        </div>
      </footer>

      {showSample && <SampleReportModal onClose={() => setShowSample(false)} onStart={() => { setShowSample(false); user ? nav("/dashboard") : nav("/register"); }} />}
      {advisorOpen && (
        <AdvisorModal
          prop={advisorProp}
          text={advisorText}
          loading={advisorLoading}
          locked={advisorLocked}
          onClose={() => setAdvisorOpen(false)}
          onUnlock={unlockResults}
        />
      )}
    </div>
  );
}

function PreviewCard({ icon: Icon, title, sub }) {
  return (
    <div className="surface-card p-4">
      <Icon size={16} strokeWidth={1.5} className="text-[--accent] mb-3" />
      <div className="font-display font-bold text-sm leading-tight">{title}</div>
      <div className="text-xs text-[--ink-muted] mt-1">{sub}</div>
    </div>
  );
}

function BlurredDealCard() {
  return (
    <div className="surface-card p-4 relative overflow-hidden">
      <div className="text-[10px] font-mono uppercase tracking-wider text-[--ink-muted] mb-1">
        Example deal
      </div>
      <div className="font-display font-bold text-sm blur-sm select-none">
        4318 Maplewood Ave
      </div>
      <div className="text-xs font-mono text-[--ink-muted] blur-sm select-none">
        Greeley, CO · 80634
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-[10px] font-mono">
        <div>
          <div className="text-[--ink-muted]">ARV</div>
          <div className="font-semibold blur-sm select-none">$285k</div>
        </div>
        <div>
          <div className="text-[--ink-muted]">MAO</div>
          <div className="font-semibold blur-sm select-none">$162k</div>
        </div>
        <div>
          <div className="text-[--ink-muted]">Profit</div>
          <div className="font-semibold text-emerald-700 blur-sm select-none">$47k</div>
        </div>
      </div>
      <div className="absolute inset-0 flex items-end p-3 bg-gradient-to-t from-white/80 to-transparent">
        <span className="text-[10px] font-mono uppercase tracking-wider text-[--ink]">Unlock with trial →</span>
      </div>
    </div>
  );
}

function MetricTooltip({ label, icon: Icon, tooltip }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`metric-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className="surface-card p-4 relative cursor-help"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
    >
      <div className="flex items-center gap-2">
        <Icon size={14} strokeWidth={1.5} className="text-[--ink-muted]" />
        <span className="text-sm font-semibold">{label}</span>
      </div>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-10 w-64 p-3 bg-[--ink] text-white text-xs leading-relaxed shadow-lg">
          {tooltip}
        </div>
      )}
    </div>
  );
}

function SampleReportModal({ onClose, onStart }) {
  return (
    <div
      data-testid="sample-report-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto border border-[--ink]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[--line] flex items-center justify-between">
          <div>
            <div className="overline">// Sample report</div>
            <h2 className="font-display text-xl font-bold tracking-tighter mt-1">
              Greeley, CO · Weld County
            </h2>
          </div>
          <button
            data-testid="close-sample-modal"
            onClick={onClose}
            className="p-2 hover:bg-[--bg-soft] border border-[--line]"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Market overview */}
          <div>
            <div className="overline mb-3">Market overview</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat label="Opportunity" value="66/100" tone="good" />
              <Stat label="Motivation" value="High" tone="good" />
              <Stat label="Saturation" value="Low" tone="good" />
              <Stat label="Cash %" value="42%" />
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm mt-3">
              <Stat label="Sold 180d" value="498" />
              <Stat label="Pop CAGR" value="+1.7%" tone="good" />
              <Stat label="Jobs YoY" value="+0.2%" />
            </div>
          </div>

          {/* Sample deal list */}
          <div>
            <div className="overline mb-3 flex items-center gap-1.5">
              <Sparkles size={11} strokeWidth={1.5} /> Top deals
            </div>
            <div className="space-y-2">
              {SAMPLE_DEALS.map((d, i) => (
                <div key={i} className="border border-[--line] p-3 flex items-center gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{d.address}</div>
                    <div className="text-xs font-mono text-[--ink-muted] truncate">
                      {d.beds}bd/{d.baths}ba · {d.sqft.toLocaleString()} sf · {d.signals.join(" · ")}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-xs text-[--ink-muted]">MAO</div>
                    <div className="font-mono font-bold text-base text-[--accent]">${d.mao.toLocaleString()}</div>
                  </div>
                  <span
                    className={`text-[10px] font-mono px-1.5 py-0.5 border ${
                      d.motivation >= 75
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                  >
                    {d.motivation}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Sample MAO/ARV */}
          <div>
            <div className="overline mb-3 flex items-center gap-1.5">
              <Calculator size={11} strokeWidth={1.5} /> Sample MAO / ARV
            </div>
            <div className="border border-[--line] p-4 text-sm font-mono space-y-1.5">
              <Row label="ARV (est.)" value="$285,000" />
              <Row label="Repairs (est.)" value="$38,000" />
              <Row label="Wholesale fee" value="$10,000" />
              <Row label="MAO @ 65%" value="$137,250" />
              <Row label="MAO @ 70%" value="$151,500" bold accent />
              <Row label="MAO @ 75%" value="$165,750" />
              <Row label="Potential profit" value="$47,200" bold pos />
            </div>
          </div>

          {/* Sample motivation breakdown */}
          <div>
            <div className="overline mb-3 flex items-center gap-1.5">
              <TrendingUp size={11} strokeWidth={1.5} /> Sample motivation breakdown
            </div>
            <div className="border border-[--line] p-4 text-sm space-y-2">
              <MotivationRow signal="Pre-foreclosure" weight="+20" />
              <MotivationRow signal="Long-term owner (18 yrs)" weight="+8" />
              <MotivationRow signal="Absentee owner" weight="+8" />
              <MotivationRow signal="High equity (67%)" weight="+5" />
              <div className="flex items-center justify-between pt-2 border-t border-[--line] text-base font-bold">
                <span>Total motivation</span>
                <span className="text-emerald-700">81 / 100</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-[--line] p-4 flex justify-end gap-2 bg-[--bg-soft]">
          <button onClick={onClose} className="btn-ghost">Close</button>
          <button data-testid="sample-modal-start" onClick={onStart} className="btn-primary flex items-center gap-2">
            Run on a real market <ArrowRight size={14} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

const SAMPLE_DEALS = [
  { address: "4318 Maplewood Ave, Greeley CO", beds: 3, baths: 2, sqft: 1480, signals: ["pre-foreclosure", "absentee"], mao: 151500, motivation: 81 },
  { address: "1207 N 23rd St, Greeley CO", beds: 4, baths: 2, sqft: 1820, signals: ["tax delinquent", "high equity"], mao: 167200, motivation: 76 },
  { address: "2918 W 18th St, Greeley CO", beds: 2, baths: 1, sqft: 980, signals: ["code violation"], mao: 94300, motivation: 68 },
];

function Stat({ label, value, tone }) {
  return (
    <div className="border border-[--line] p-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-[--ink-muted] mb-1">
        {label}
      </div>
      <div
        className={`font-display font-bold text-lg ${
          tone === "good" ? "text-emerald-700" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function Row({ label, value, bold, accent, pos }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[--ink-muted]">{label}</span>
      <span
        className={`${bold ? "font-bold" : ""} ${
          accent ? "text-[--accent]" : pos ? "text-emerald-700" : ""
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function MotivationRow({ signal, weight }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span>{signal}</span>
      <span className="font-mono font-semibold text-emerald-700">{weight}</span>
    </div>
  );
}

function DemoStat({ icon: Icon, label, value, tone }) {
  return (
    <div className="surface-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={12} strokeWidth={1.5} className="text-[--ink-muted]" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-[--ink-muted]">
          {label}
        </span>
      </div>
      <div
        className={`font-display text-2xl font-black tracking-tighter ${
          tone === "good" ? "text-emerald-700" : tone === "weak" ? "text-rose-700" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function DemoDealCard({ prop, onClick }) {
  const signalLabels = (prop.distress_signals || []).slice(0, 3);
  return (
    <button
      type="button"
      data-testid={`demo-deal-${prop.redacted_address?.replace(/\s+/g, "-").toLowerCase()}`}
      onClick={onClick}
      className="surface-card p-4 text-left hover:border-[--ink] transition-colors group cursor-pointer relative"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="font-display font-bold text-sm truncate">{prop.redacted_address}</div>
          <div className="text-[10px] font-mono text-[--ink-muted] mt-0.5">
            {prop.city}, {prop.state} · {prop.beds}bd/{prop.baths}ba · {prop.sqft || "—"} sf
          </div>
        </div>
        <span
          className={`text-[10px] font-mono px-1.5 py-0.5 border ${
            prop.motivation_score >= 75
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : prop.motivation_score >= 55
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-rose-50 text-rose-700 border-rose-200"
          }`}
        >
          {prop.motivation_score}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs font-mono mt-3 border-t border-[--line] pt-3">
        <div>
          <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">ARV</div>
          <div className="font-semibold">${((prop.estimated_arv || 0) / 1000).toFixed(0)}k</div>
        </div>
        <div>
          <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">MAO</div>
          <div className="font-semibold text-[--accent]">${((prop.mao_70 || 0) / 1000).toFixed(0)}k</div>
        </div>
        <div>
          <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">Profit</div>
          <div className="font-semibold text-emerald-700">${((prop.potential_profit || 0) / 1000).toFixed(0)}k</div>
        </div>
      </div>
      {signalLabels.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {signalLabels.map((s) => (
            <span key={s} className="text-[9px] font-mono px-1.5 py-0.5 bg-[--bg-soft] text-[--ink-muted] uppercase tracking-wider">
              {s.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      )}
      <div className="mt-3 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1 text-[--accent] opacity-70 group-hover:opacity-100 transition-opacity">
        <Sparkles size={10} strokeWidth={1.5} />
        Ask the AI advisor
      </div>
    </button>
  );
}

function AdvisorModal({ prop, text, loading, locked, onClose, onUnlock }) {
  return (
    <div
      data-testid="advisor-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-xl w-full max-h-[92vh] overflow-y-auto border border-[--ink]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[--line] flex items-center justify-between">
          <div>
            <div className="overline flex items-center gap-1.5">
              <Sparkles size={11} strokeWidth={1.5} /> AI advisor
            </div>
            <h2 className="font-display text-xl font-bold tracking-tighter mt-1">
              {prop?.redacted_address}
            </h2>
            <div className="text-xs font-mono text-[--ink-muted] mt-0.5">
              {prop?.city}, {prop?.state} · motivation {prop?.motivation_score}/100
            </div>
          </div>
          <button
            data-testid="close-advisor"
            onClick={onClose}
            className="p-2 hover:bg-[--bg-soft] border border-[--line]"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-5">
          {loading && (
            <div className="text-sm text-[--ink-muted] flex items-center gap-2 py-4" data-testid="advisor-loading">
              <span className="w-1.5 h-1.5 bg-[--accent] rounded-full animate-pulse" />
              Claude is analyzing this property…
            </div>
          )}
          {!loading && text && (
            <div
              data-testid="advisor-text"
              className={`text-sm leading-relaxed whitespace-pre-wrap p-4 border ${
                locked
                  ? "bg-amber-50 border-amber-200 text-amber-900"
                  : "bg-[--bg-soft] border-[--line]"
              }`}
            >
              {text}
            </div>
          )}
        </div>
        <div className="border-t border-[--line] p-4 flex justify-end gap-2 bg-[--bg-soft]">
          <button onClick={onClose} className="btn-ghost">Close</button>
          <button
            data-testid="advisor-unlock"
            onClick={onUnlock}
            className="btn-primary flex items-center gap-2"
          >
            Unlock full advisor <ArrowRight size={14} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
